import { useCallback, useState } from 'react'
import { PermissionEnum } from 'api/userPermissions'
import { AuthGuard, useAuthContext } from 'components/auth'
import { useShiftLeadMinWorkers } from 'hooks/useShiftLeadMinWorkers'
import { useEligibleLeadShifts, useLeadShifts } from 'queries/shift'
import { QueryData } from 'queries/types'
import { Box, Button, Flex, Heading, Message, Text } from '@workwhile/ui'
import { CreateLeadShiftsModal } from './CreateLeadShiftsModal'
import { LinkLeadShiftsFlow } from './LinkLeadShiftsFlow'

interface Props {
  companyId: number
  shiftId: number
  timezone: string
  workersNeeded: number
  canCreateLeadShifts: boolean
  leadCompanyId?: number
  defaultLeadPositionId?: number
  hasLeadPosition: boolean
  hasBillRate: boolean
  paymentType: QueryData<typeof useLeadShifts>['paymentType']
}

export const MissingShiftLeads = ({
  companyId,
  shiftId,
  timezone,
  workersNeeded,
  canCreateLeadShifts,
  leadCompanyId,
  defaultLeadPositionId,
  hasLeadPosition,
  hasBillRate,
  paymentType,
}: Props) => {
  const workersPerShiftLead = useShiftLeadMinWorkers()
  const { hasPermission } = useAuthContext()
  const [isLinkFlowOpen, setIsLinkFlowOpen] = useState(false)

  const isMissingLeads = workersNeeded >= workersPerShiftLead
  // Only look up eligible lead shifts when the link button could be shown
  const canLinkLeadShifts =
    isMissingLeads &&
    canCreateLeadShifts &&
    hasLeadPosition &&
    hasPermission(PermissionEnum.ManageShifts)
  const { data: eligibleLeadShifts = [] } = useEligibleLeadShifts(
    { companyId, shiftId },
    { enabled: canLinkLeadShifts }
  )

  const openLinkFlow = useCallback(() => setIsLinkFlowOpen(true), [])
  const closeLinkFlow = useCallback(() => setIsLinkFlowOpen(false), [])

  if (!isMissingLeads) return null

  return (
    <Box mb={2}>
      <Heading level={2} fontWeight={2}>
        Shift Leads
      </Heading>
      {canCreateLeadShifts ? (
        <Message variant="warning" systemLevel>
          <Box>
            There are no shift leads linked to this shift. Your company policy
            requires 1 lead per {workersPerShiftLead} workers.
          </Box>
          <AuthGuard.Permission value={PermissionEnum.ManageShifts}>
            <Flex mt={2} alignItems="center">
              {hasBillRate ? (
                <>
                  <Button kind="medium" disabled>
                    Create lead shifts
                  </Button>
                  <Text ml={2}>
                    Lead shifts can't be created for shifts paid at a bill rate.
                    Contact support to get them set up.
                  </Text>
                </>
              ) : (
                <CreateLeadShiftsModal
                  shiftId={shiftId}
                  workersNeeded={workersNeeded}
                  leadCompanyId={leadCompanyId}
                  defaultLeadPositionId={defaultLeadPositionId}
                  paymentType={paymentType}
                  trigger={<Button kind="medium">Create lead shifts</Button>}
                />
              )}
              {canLinkLeadShifts && eligibleLeadShifts.length > 0 ? (
                <Box ml={2}>
                  <Button
                    kind="medium"
                    variant="secondary"
                    onClick={openLinkFlow}
                  >
                    Link lead shift
                  </Button>
                </Box>
              ) : null}
            </Flex>
          </AuthGuard.Permission>
        </Message>
      ) : (
        <Message variant="error" systemLevel>
          This shift has no shift leads. Your policy requires 1 lead per{' '}
          {workersPerShiftLead} workers, but shift leads can't be created
          without a linked W2 company. Contact support to get one set up.
        </Message>
      )}
      {canLinkLeadShifts ? (
        <LinkLeadShiftsFlow
          companyId={companyId}
          shiftId={shiftId}
          timezone={timezone}
          open={isLinkFlowOpen}
          onClose={closeLinkFlow}
        />
      ) : null}
    </Box>
  )
}
